/**
 * Contamination Detection - Real-time workspace contamination monitoring and filtering
 */

import { supabase } from '@/integrations/supabase/client';
import { secureLog, safeLog, canSeeSecurityDetails } from './authorizationUtils';
import type { ContaminationAlert, ContaminationReport } from '../types';

// Contamination pattern definitions
const WEIGHT_LOSS_TERMS = [
  // Core weight loss terms
  'weight loss', 'weight-loss', 'lose weight', 'losing weight', 'weight reduction',
  'fat loss', 'fat burning', 'burn fat', 'belly fat', 'stomach fat', 'abdominal fat',
  'slim', 'slimming', 'obesity', 'overweight', 'body weight', 'shed pounds',
  
  // Diet and nutrition
  'diet', 'dieting', 'calorie', 'calories', 'low calorie', 'caloric', 'nutrition',
  'meal plan', 'diet plan', 'healthy eating', 'eating plan', 'food plan',
  'keto', 'ketogenic', 'paleo', 'intermittent fasting', 'fasting', 'detox',
  'low carb', 'protein', 'supplements', 'vitamins', 'minerals',
  
  // Exercise and fitness
  'fitness', 'exercise', 'workout', 'training', 'gym', 'cardio', 'aerobic',
  'strength training', 'muscle building', 'bodybuilding', 'running', 'jogging',
  'yoga', 'pilates', 'cycling', 'swimming', 'sports', 'physical activity',
  
  // Health and metabolism
  'metabolism', 'metabolic', 'metabolic rate', 'BMI', 'body mass index',
  'health', 'healthy', 'wellness', 'wellbeing', 'medical', 'doctor',
  'blood pressure', 'cholesterol', 'diabetes', 'heart health',
  
  // Additional aggressive patterns
  'weight', 'loss', 'diet', 'fat', 'slim', 'calories', 'fitness', 'exercise',
  'metabolism', 'nutrition', 'healthy', 'belly', 'stomach', 'protein',
  'supplements', 'workout', 'gym', 'cardio', 'muscle', 'body'
];

const JEWELRY_TERMS = [
  // Core jewelry terms
  'jewelry', 'jewellery', 'jewels', 'ornament', 'ornaments', 'accessory', 'accessories',
  'adornment', 'adornments', 'trinket', 'trinkets', 'bijou', 'bijoux',
  
  // Types of jewelry
  'necklace', 'necklaces', 'earring', 'earrings', 'bracelet', 'bracelets', 
  'ring', 'rings', 'pendant', 'pendants', 'charm', 'charms', 'brooch', 'brooches',
  'anklet', 'anklets', 'chain', 'chains', 'choker', 'chokers', 'cufflink', 'cufflinks',
  'tiara', 'tiaras', 'crown', 'crowns', 'hairpin', 'hairpins', 'hair clip', 'hair clips',
  
  // Materials and techniques
  'terracotta', 'clay', 'ceramic', 'gold', 'silver', 'platinum', 'copper', 'brass',
  'gemstone', 'gemstones', 'diamond', 'diamonds', 'pearl', 'pearls', 'ruby', 'emerald',
  'sapphire', 'topaz', 'amethyst', 'quartz', 'crystal', 'crystals', 'bead', 'beads',
  'beaded', 'beading', 'wire wrapped', 'handmade', 'handcrafted', 'artisan', 'artisanal'
];

/**
 * Emergency filter to remove contaminated content from any data array
 */
export function emergencyFilterWorkspaceData<T extends any[]>(
  data: T,
  workspaceId: string,
  options: {
    logFiltering?: boolean;
    fieldName?: string;
  } = {}
): T {
  const { logFiltering = false, fieldName = 'unknown' } = options;
  
  if (!data || data.length === 0) {
    return data;
  }

  const workspaceName = workspaceId.toLowerCase();
  
  const filteredData = data.filter(item => {
    const textFields = [
      item.keyword_text,
      item.title,
      item.content,
      item.description,
      item.category_reasoning,
      item.primary_keyword,
      item.secondary_keywords,
      item.cluster_name,
      item.related_terms,
      item.semantic_variations,
      item.question_variations,
      item.content_focus,
      item.content_outline
    ].filter(Boolean);
    
    const combinedContent = textFields.join(' ').toLowerCase();
    
    if (combinedContent.trim().length === 0) {
      return true;
    }
    
    let isContaminated = false;
    
    // SPECIFIC TERRACOTTA JEWELRY WORKSPACE - ULTRA AGGRESSIVE FILTERING
    if (workspaceId === '6cf120c4-1d18-4c6b-8f4b-6de27877c2e9' || 
        workspaceName.includes('terracotta') || 
        workspaceName.includes('jewelry') || 
        workspaceName.includes('jewellery')) {
      
      isContaminated = WEIGHT_LOSS_TERMS.some(term => combinedContent.includes(term.toLowerCase()));
      
      if (isContaminated && logFiltering) {
        secureLog('warn', `[ContaminationDetection] Weight loss content filtered from jewelry workspace:`, {
          workspaceId,
          fieldName,
          item: item.keyword_text || item.title || item.cluster_name,
          matchedTerms: WEIGHT_LOSS_TERMS.filter(term => combinedContent.includes(term.toLowerCase()))
        });
      }
    }
    
    // LYME DISEASE WORKSPACES - Allow all health content, filter jewelry
    if (workspaceId === 'e9626a1c-33dc-4acd-b5e0-c06a0191c04f' ||
        workspaceId === 'd0cb3fcb-66d9-4577-af32-e3a834977161' ||
        workspaceName.toLowerCase().includes('lyme') ||
        workspaceName.toLowerCase().includes('disease')) {
      
      isContaminated = JEWELRY_TERMS.some(term => combinedContent.includes(term.toLowerCase()));
      
      if (isContaminated && logFiltering) {
        secureLog('warn', `[ContaminationDetection] Jewelry content filtered from health/disease workspace:`, {
          workspaceId,
          fieldName,
          item: item.keyword_text || item.title || item.cluster_name,
          matchedTerms: JEWELRY_TERMS.filter(term => combinedContent.includes(term.toLowerCase()))
        });
      }
    }
    // WEIGHT LOSS WORKSPACES - Filter out jewelry content 
    else if (workspaceId === '4b4648c8-8bc7-40a2-bc71-6bcf53aecc9e' || 
        workspaceId === 'ddbb213d-8574-46ef-ba4a-42a6f9acd500' || 
        workspaceName.includes('weight') || 
        workspaceName.includes('diet') || 
        workspaceName.includes('fitness') ||
        workspaceName.includes('fat')) {
      
      isContaminated = JEWELRY_TERMS.some(term => combinedContent.includes(term.toLowerCase()));
      
      if (isContaminated && logFiltering) {
        secureLog('warn', `[ContaminationDetection] Jewelry content filtered from weight loss workspace:`, {
          workspaceId,
          fieldName,
          item: item.keyword_text || item.title || item.cluster_name,
          matchedTerms: JEWELRY_TERMS.filter(term => combinedContent.includes(term.toLowerCase()))
        });
      }
    }
    
    return !isContaminated;
  });

  if (logFiltering && filteredData.length !== data.length) {
    const filteredCount = data.length - filteredData.length;
    const contaminationRate = ((filteredCount / data.length) * 100).toFixed(1);
    
    secureLog('warn', `[ContaminationDetection] Contamination cleanup report:`, {
      workspaceId,
      fieldName,
      totalRecords: data.length,
      filteredRecords: filteredCount,
      remainingRecords: filteredData.length,
      contaminationRate: `${contaminationRate}%`
    });
    
    if (filteredCount > 0) {
      safeLog(
        `[ContaminationDetection] Successfully filtered ${filteredCount} contaminated items from ${fieldName}`,
        `[ContaminationDetection] Data filtering applied successfully`
      );
    }
  } else if (logFiltering && data.length > 0) {
    safeLog(
      `[ContaminationDetection] No contamination detected in ${fieldName} for workspace ${workspaceId}`,
      `[ContaminationDetection] Data validation completed successfully`
    );
  }

  return filteredData as T;
}

/**
 * Monitor workspace for contamination and return alerts
 */
export async function monitorWorkspaceContamination(
  workspaceId: string,
  userId: string,
  options: {
    enableAlerts?: boolean;
    logToConsole?: boolean;
    checkAllTables?: boolean;
  } = {}
): Promise<ContaminationReport> {
  const { enableAlerts = true, logToConsole = true, checkAllTables = true } = options;
  
  const alerts: ContaminationAlert[] = [];
  let workspaceName = 'Unknown';
  
  try {
    // Get workspace info
    const { data: workspace } = await supabase
      .from('workspaces')
      .select('name')
      .eq('id', workspaceId)
      .single();
    
    if (workspace) {
      workspaceName = workspace.name;
    }
    
    // Check each table for contamination
    const tablesToCheck = checkAllTables 
      ? ['keyword_categorization', 'keyword_clusters', 'content_briefs']
      : ['keyword_clusters', 'content_briefs'];
    
    for (const tableName of tablesToCheck) {
      const alert = await checkTableContamination(
        workspaceId,
        workspaceName,
        tableName,
        WEIGHT_LOSS_TERMS,
        JEWELRY_TERMS
      );
      
      if (alert) {
        alerts.push(alert);
        
        if (logToConsole) {
          await secureLog('warn', `[ContaminationDetection] Contamination detected:`, alert);
        }
      }
    }
    
    // Calculate overall status
    const totalContaminationRate = alerts.length > 0 
      ? alerts.reduce((sum, alert) => sum + alert.contaminationRate, 0) / alerts.length
      : 0;
    
    let overallStatus: 'clean' | 'contaminated' | 'critical' = 'clean';
    if (totalContaminationRate > 50) {
      overallStatus = 'critical';
    } else if (totalContaminationRate > 0) {
      overallStatus = 'contaminated';
    }
    
    // Generate recommendations
    const recommendations = generateRecommendations(alerts, workspaceName);
    
    const report: ContaminationReport = {
      workspaceId,
      workspaceName,
      alerts,
      overallStatus,
      contaminationRate: totalContaminationRate,
      recommendations
    };
    
    if (logToConsole) {
      const canSeeDetails = await canSeeSecurityDetails();
      if (canSeeDetails) {
        console.log(`[ContaminationDetection] Workspace contamination report:`, report);
      } else {
        console.log(`[ContaminationDetection] Workspace validation completed`);
      }
    }
    
    return report;
    
  } catch (error) {
    console.error('[ContaminationDetection] Error monitoring contamination:', error);
    return {
      workspaceId,
      workspaceName,
      alerts: [],
      overallStatus: 'clean',
      contaminationRate: 0,
      recommendations: ['Error occurred during monitoring - manual check recommended']
    };
  }
}

/**
 * Check specific table for contamination
 */
async function checkTableContamination(
  workspaceId: string,
  workspaceName: string,
  tableName: string,
  weightLossTerms: string[],
  jewelryTerms: string[]
): Promise<ContaminationAlert | null> {
  
  try {
    const { data: records, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('workspace_id', workspaceId);
    
    if (error || !records || records.length === 0) {
      return null;
    }
    
    const isJewelryWorkspace = workspaceName.toLowerCase().includes('terracotta') ||
                              workspaceName.toLowerCase().includes('jewelry') ||
                              workspaceName.toLowerCase().includes('jewellery');
    
    const isWeightLossWorkspace = workspaceName.toLowerCase().includes('weight') ||
                                 workspaceName.toLowerCase().includes('diet') ||
                                 workspaceName.toLowerCase().includes('fitness') ||
                                 workspaceName.toLowerCase().includes('fat');
    
    let contaminatedRecords = 0;
    const detectedTerms: string[] = [];
    
    for (const record of records) {
      const textContent = extractTextContent(record).toLowerCase();
      
      if (isJewelryWorkspace) {
        const contaminatingTerms = weightLossTerms.filter(term => 
          textContent.includes(term.toLowerCase())
        );
        
        if (contaminatingTerms.length > 0) {
          contaminatedRecords++;
          detectedTerms.push(...contaminatingTerms);
        }
      } else if (isWeightLossWorkspace) {
        const contaminatingTerms = jewelryTerms.filter(term => 
          textContent.includes(term.toLowerCase())
        );
        
        if (contaminatingTerms.length > 0) {
          contaminatedRecords++;
          detectedTerms.push(...contaminatingTerms);
        }
      }
    }
    
    if (contaminatedRecords > 0) {
      const contaminationRate = (contaminatedRecords / records.length) * 100;
      const uniqueTerms = [...new Set(detectedTerms)];
      
      let severity: ContaminationAlert['severity'] = 'low';
      if (contaminationRate > 75) severity = 'critical';
      else if (contaminationRate > 50) severity = 'high';
      else if (contaminationRate > 25) severity = 'medium';
      
      return {
        severity,
        workspaceId,
        workspaceName,
        tableName,
        contaminatedRecords,
        totalRecords: records.length,
        contaminationRate,
        detectedTerms: uniqueTerms,
        timestamp: new Date(),
        message: `${contaminatedRecords}/${records.length} records (${contaminationRate.toFixed(1)}%) contain inappropriate content for ${workspaceName}`
      };
    }
    
    return null;
    
  } catch (error) {
    console.error(`[ContaminationDetection] Error checking ${tableName}:`, error);
    return null;
  }
}

/**
 * Extract text content from a record for analysis
 */
function extractTextContent(record: any): string {
  const textFields = [
    record.keyword_text,
    record.title,
    record.content,
    record.description,
    record.category_reasoning,
    record.primary_keyword,
    record.secondary_keywords,
    record.cluster_name,
    record.related_terms,
    record.semantic_variations,
    record.question_variations,
    record.content_focus,
    record.content_outline
  ].filter(Boolean);
  
  return textFields.join(' ');
}

/**
 * Generate recommendations based on contamination alerts
 */
function generateRecommendations(
  alerts: ContaminationAlert[],
  workspaceName: string
): string[] {
  const recommendations: string[] = [];
  
  if (alerts.length === 0) {
    recommendations.push('✅ Workspace is clean - no contamination detected');
    return recommendations;
  }
  
  const criticalAlerts = alerts.filter(a => a.severity === 'critical');
  const highAlerts = alerts.filter(a => a.severity === 'high');
  
  if (criticalAlerts.length > 0) {
    recommendations.push('🚨 CRITICAL: Immediate database cleanup required');
    recommendations.push('🔄 Run emergency filtering on all affected tables');
    recommendations.push('🔍 Investigate AI workflow for workspace assignment issues');
  }
  
  if (highAlerts.length > 0) {
    recommendations.push('⚠️ HIGH: Database cleanup recommended');
    recommendations.push('📊 Review data migration and workspace assignment logic');
  }
  
  recommendations.push('🛡️ Enable emergency filtering for immediate UI protection');
  recommendations.push('📈 Set up regular contamination monitoring');
  
  return recommendations;
}

/**
 * Generate a contamination report for a workspace
 */
export async function generateContaminationReport(
  workspaceId: string,
  userId: string
): Promise<ContaminationReport> {
  return await monitorWorkspaceContamination(workspaceId, userId, {
    enableAlerts: true,
    logToConsole: true,
    checkAllTables: true
  });
}

/**
 * Schedule regular contamination monitoring
 */
export function scheduleContaminationMonitoring(
  workspaceId: string,
  userId: string,
  intervalMinutes: number = 30
): () => void {
  const interval = setInterval(async () => {
    const report = await monitorWorkspaceContamination(workspaceId, userId, {
      enableAlerts: true,
      logToConsole: true,
      checkAllTables: true
    });
    
    if (report.overallStatus !== 'clean') {
      await secureLog('warn', `[ContaminationDetection] Scheduled check found contamination in ${report.workspaceName}`, report);
    }
  }, intervalMinutes * 60 * 1000);
  
  return () => clearInterval(interval);
}